import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send } from 'lucide-react';
import ChatMessage from './ChatMessage';
import { sendMessage, autosaveSession } from '../../services/insightLabService';
import { trackInsightLabMessageSent } from '../../services/analytics';

const ChatView = ({ session, messages, setMessages, onSessionUpdate }) => {
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const chatContainerRef = useRef(null);
  const chatEndRef = useRef(null);
  const autosaveTimeoutRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  const debouncedAutosave = useCallback((updatedMessages) => {
    if (autosaveTimeoutRef.current) clearTimeout(autosaveTimeoutRef.current);
    autosaveTimeoutRef.current = setTimeout(async () => {
      try {
        await autosaveSession(session.id, updatedMessages);
      } catch (err) {
        console.error('Autosave failed:', err);
      }
    }, 500);
  }, [session?.id]);

  useEffect(() => {
    return () => {
      if (autosaveTimeoutRef.current) clearTimeout(autosaveTimeoutRef.current);
    };
  }, []);

  const handleSend = async (messageText) => {
    const text = messageText || inputMessage.trim();
    if (!text || isLoading || !session) return;

    setInputMessage('');
    setIsLoading(true);

    // Optimistically add user message
    const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
    const updatedMessages = [...messages, userMsg];
    trackInsightLabMessageSent({ session_message_index: updatedMessages.filter(m => m.role === 'user').length });
    setMessages(updatedMessages);

    try {
      const { aiMessage } = await sendMessage(session.id, text);
      const finalMessages = [...updatedMessages, aiMessage];
      setMessages(finalMessages);
      debouncedAutosave(finalMessages);

      // If this was the first user message, the title was generated server-side
      const userMsgCount = finalMessages.filter(m => m.role === 'user').length;
      if (userMsgCount === 1 && onSessionUpdate) {
        onSessionUpdate();
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      // Add error message
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Sorry, I encountered an error: ${err.message}. Please try again.`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSend();
  };

  const handleFollowUp = (suggestion) => {
    handleSend(suggestion);
  };

  const visibleMessages = messages.filter(m => m.role !== 'system');

  if (!session) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <p className="text-lg mb-2">Select a session or start a new one</p>
          <p className="text-sm">Your coaching conversations are saved and resumable</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      <div ref={chatContainerRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 space-y-4">
        {visibleMessages.map((msg, idx) => (
          <ChatMessage
            key={idx}
            message={msg}
            isLast={idx === visibleMessages.length - 1}
            onFollowUp={handleFollowUp}
          />
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="max-w-xl rounded-lg px-4 py-3 bg-secondary text-secondary-foreground border border-border flex items-center gap-1">
              <span className="typing-dot" />
              <span className="typing-dot" style={{ animationDelay: '0.2s' }} />
              <span className="typing-dot" style={{ animationDelay: '0.4s' }} />
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="flex-shrink-0 p-4 border-t border-border bg-card z-10">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Ask about your trading patterns, psychology, or performance..."
            className="flex-1 border border-border rounded-lg px-4 py-3 bg-input text-foreground placeholder:text-muted-foreground"
            disabled={isLoading}
          />
          <button
            type="submit"
            className="bg-crimson text-white p-3 rounded-lg hover:bg-crimson/90 disabled:opacity-50"
            disabled={!inputMessage.trim() || isLoading}
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatView;
